import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Input } from "@/components/ui/input";
import { Search, RefreshCw, CheckCircle2, Plus } from 'lucide-react';
import { fetchKnitApps, refreshAtsAppsCache, deactivateIntegration } from '@/services/knitService';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import KnitAuth, { KnitAuthRef } from "@/components/integrations/KnitAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { sendAtsRequestEmail } from "@/services/emailService";

// Define the ATS integration type
interface ATSIntegration {
  id: string;
  name: string;
  logo: string;
  category: string;
  integrated?: boolean;
}

// Define the external ATS list format from JSON
interface ExternalATSIntegration {
  category: string;
  label: string;
  id: string;
  logo_short: string;
  logo_full: string;
}

interface KnitIntegration {
  id: string;
  integration_id: string;
  app_id: string;
  active: boolean;
  metadata?: any;
}

interface CurrentIntegration {
  id: string;
  integrationId: string;
  appId: string;
  appName: string;
  logo: string;
}

interface IntegrationsTabProps {
  atsList?: ExternalATSIntegration[];
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ atsList = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [integrations, setIntegrations] = useState<ATSIntegration[]>([]);
  const [userIntegrations, setUserIntegrations] = useState<KnitIntegration[]>([]);
  const [currentIntegration, setCurrentIntegration] = useState<CurrentIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const knitAuthRef = useRef<KnitAuthRef>(null);
  
  // Request ATS dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [atsName, setAtsName] = useState('');
  const [atsWebsite, setAtsWebsite] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Deactivation dialog state
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // Listen for successful integrations and refresh the list
  useEffect(() => {
    // Define a function to handle the custom event
    const handleIntegrationSuccess = (event: Event) => {
      const customEvent = event as CustomEvent<{
        appId: string;
        integrationId: string;
      }>;
      //console.log('[IntegrationsTab] Integration success event detected', customEvent.detail);
      //console.log('[IntegrationsTab] Refreshing integrations list');
      fetchUserIntegrations();
    };
    
    // Listen for our custom event
    //console.log('[IntegrationsTab] Setting up event listener for knit:integration-success');
    window.addEventListener('knit:integration-success', handleIntegrationSuccess);
    
    return () => {
      //console.log('[IntegrationsTab] Removing event listener for knit:integration-success');
      window.removeEventListener('knit:integration-success', handleIntegrationSuccess);
    };
  }, []);
  
  // Memoize function to prevent unnecessary re-renders
  const fetchUserIntegrations = useCallback(async () => {
    //console.log('[IntegrationsTab] Fetching user integrations from database');
    try {
      const { data, error } = await supabase
        .from('knit_integrations')
        .select('id, integration_id, app_id, active, metadata')
        .eq('active', true);
        
      if (error) {
        //console.error('[IntegrationsTab] Error fetching integrations:', error);
        throw error;
      }
      
      //console.log('[IntegrationsTab] User integrations fetched:', data);
      setUserIntegrations(data || []);
      
      // If there's an active integration, set it as current
      if (data && data.length > 0) {
        // Use the first active integration
        const activeIntegration = data[0];
        //console.log('[IntegrationsTab] Found active integration:', activeIntegration);
        
        // Check if we have cached ATS apps to get the logo and proper name
        let logo = `/ats-logos/${activeIntegration.app_id}.png`; // Default
        let appName = activeIntegration.app_id;
        
        // Check localStorage for cached ATS apps
        const cachedData = localStorage.getItem('knit_ats_apps_cache');
        if (cachedData) {
          try {
            const { apps } = JSON.parse(cachedData);
            const cachedApp = apps.find((app: any) => app.id === activeIntegration.app_id);
            if (cachedApp) {
              logo = cachedApp.logo_short || logo;
              // Apply proper naming for TeamTailor
              appName = activeIntegration.app_id === 'teamtailor' ? 'Team Tailor' : cachedApp.label;
              //onsole.log('[IntegrationsTab] Found cached app data:', cachedApp);
            }
          } catch (e) {
            //console.error('[IntegrationsTab] Error parsing cached ATS data:', e);
          }
        }
        
        //console.log('[IntegrationsTab] Setting current integration:', { appId: activeIntegration.app_id, appName });
        setCurrentIntegration({
          id: activeIntegration.id,
          integrationId: activeIntegration.integration_id,
          appId: activeIntegration.app_id,
          appName: appName,
          logo: logo,
        });
      } else {
        //console.log('[IntegrationsTab] No active integrations found, setting currentIntegration to null');
        setCurrentIntegration(null);
      }
    } catch (error) {
      //console.error('[IntegrationsTab] Failed to fetch user integrations:', error);
    }
  }, []);
  
  // Memoize function to prevent unnecessary re-renders
  const loadAtsApps = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Use passed atsList prop if available
      if (atsList.length > 0) {
        // Transform external format to internal ATSIntegration format
        const transformedApps = atsList.map(app => {
          // Apply proper naming for TeamTailor
          const name = app.id === 'teamtailor' ? 'Team Tailor' : app.label;
          
          return {
            id: app.id,
            name: name,
            logo: app.logo_short || `/ats-logos/${app.id}.png`,
            category: app.category
          };
        });
        
        setIntegrations(transformedApps);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      // Fall back to fetching from API if no prop is passed
      const apps = await fetchKnitApps(forceRefresh);
      
      // Transform to ATSIntegration format
      const atsApps = apps.map(app => {
        // Apply proper naming for TeamTailor
        const name = app.id === 'teamtailor' ? 'Team Tailor' : app.label;
        
        return {
          id: app.id,
          name: name,
          logo: app.logo_short || `/ats-logos/${app.id}.png`,
          category: app.category
        };
      });
        
      setIntegrations(atsApps);
      
      // Update current integration app name if exists
      if (currentIntegration) {
        const matchingApp = atsApps.find(app => app.id === currentIntegration.appId);
        if (matchingApp) {
          setCurrentIntegration(prev => {
            if (!prev) return null;
            return {
              ...prev,
              appName: matchingApp.name,
              logo: matchingApp.logo
            };
          });
        }
      }
      
      if (forceRefresh) {
        toast.success("ATS integrations refreshed successfully");
      }
    } catch (error) {
      console.error('Failed to fetch ATS apps:', error);
      
      if (forceRefresh) {
        toast.error("Failed to refresh ATS integrations. Please try signing out and back in if the problem persists.");
      } else if (integrations.length === 0) {
        // Fallback to sample data if API call fails and no cached data
        const updatedSamples = sampleIntegrations.map(app => {
          // Apply proper naming for TeamTailor in sample data too
          if (app.id === 'teamtailor') {
            return { ...app, name: 'Team Tailor' };
          }
          return app;
        });
        
        setIntegrations(updatedSamples);
        
        // Update current integration app name if exists
        if (currentIntegration) {
          const matchingApp = updatedSamples.find(app => app.id === currentIntegration.appId);
          if (matchingApp) {
            setCurrentIntegration(prev => {
              if (!prev) return null;
              return {
                ...prev,
                appName: matchingApp.name,
                logo: matchingApp.logo
              };
            });
          }
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [atsList.length, currentIntegration, integrations.length]);
  
  // Force refresh of ATS apps
  const handleRefresh = useCallback(() => {
    loadAtsApps(true);
    fetchUserIntegrations();
  }, [loadAtsApps, fetchUserIntegrations]);
  
  // Initialize data on component mount - only if needed
  useEffect(() => {
    // If we already have integrations loaded, don't reload
    if (integrations.length > 0 && !refreshing) {
      return;
    }
    
    const init = async () => {
      try {
        await Promise.all([
          fetchUserIntegrations(),
          loadAtsApps()
        ]);
      } catch (error) {
        console.error('Error initializing integrations tab:', error);
        toast.error("Error loading integrations. Please refresh the page.");
      }
    };
    
    init();
  }, [atsList, integrations.length, refreshing, fetchUserIntegrations, loadAtsApps]);
  
  // Handle submitting ATS request
  const handleSubmitAtsRequest = async () => {
    if (!atsName.trim()) {
      toast.error("Please enter the ATS name");
      return;
    }
    
    if (!atsWebsite.trim()) {
      toast.error("Please enter the ATS website URL");
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Get user and organization data
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("User not authenticated");
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('*, organizations(*)')
        .eq('id', session.user.id)
        .single();
        
      // Send email
      const success = await sendAtsRequestEmail({
        atsName,
        atsWebsite,
        message: requestMessage,
        userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
        userEmail: session.user.email || '',
        userId: session.user.id,
        organizationName: profile?.organizations?.name || '',
        organizationId: profile?.organization_id || ''
      });
      
      if (success) {
        toast.success("Your request has been sent. We'll contact you soon!");
        setRequestDialogOpen(false);
        
        // Reset form
        setAtsName('');
        setAtsWebsite('');
        setRequestMessage('');
      }
    } catch (error) {
      console.error('Failed to submit ATS request:', error);
      toast.error("Failed to send request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle integration deactivation
  const handleDeactivateIntegration = async () => {
    if (!currentIntegration) return;
    
    try {
      setDeactivating(true);
      
      const result = await deactivateIntegration(currentIntegration.integrationId);
      
      if (result.success) {
        toast.success("Integration deactivated successfully");
        
        // Update the userIntegrations state to mark the integration as inactive
        setUserIntegrations(prevIntegrations => 
          prevIntegrations.map(integration => 
            integration.id === currentIntegration.id 
              ? { ...integration, active: false } 
              : integration
          )
        );
        
        setCurrentIntegration(null);
        setDeactivateDialogOpen(false);
        fetchUserIntegrations(); // Refresh the list
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Failed to deactivate integration:', error);
      toast.error("Failed to deactivate integration: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setDeactivating(false);
    }
  };
  
  // Sample ATS integrations data as fallback
  const sampleIntegrations: ATSIntegration[] = [
    { id: 'keka', name: 'Keka ATS', logo: '/ats-logos/keka.png', category: 'ATS' },
    { id: 'darwinbox', name: 'Darwinbox ATS', logo: '/ats-logos/darwinbox.png', category: 'ATS' },
    { id: 'lever', name: 'Lever ATS', logo: '/ats-logos/lever.png', category: 'ATS' },
    { id: 'greenhouse', name: 'Greenhouse', logo: '/ats-logos/greenhouse.png', category: 'ATS' },
    { id: 'workday', name: 'Workday', logo: '/ats-logos/workday.png', category: 'ATS' },
    { id: 'ashby', name: 'Ashby ATS', logo: '/ats-logos/ashby.png', category: 'ATS' },
    { id: 'bamboohr', name: 'BambooHR ATS', logo: '/ats-logos/bamboohr.png', category: 'ATS' },
    { id: 'fountain', name: 'Fountain', logo: '/ats-logos/fountain.png', category: 'ATS' }
  ];
  
  // Combine integrations with user integration status
  const integrationsWithStatus = useMemo(() => {
    return integrations.map(integration => ({
      ...integration,
      integrated: userIntegrations.some(
        ui => ui.app_id === integration.id && ui.active
      )
    }));
  }, [integrations, userIntegrations]);
  
  // Filter integrations based on search query
  const filteredIntegrations = useMemo(() => {
    if (!searchQuery) return integrationsWithStatus;
    
    return integrationsWithStatus.filter(integration => 
      integration.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, integrationsWithStatus]);

  // Handle integration click
  const handleIntegrationClick = (integration: ATSIntegration) => {
    // If the app is already integrated, simply show a toast
    if (integration.integrated) {
      toast.info(`${integration.name} is already integrated`);
      return;
    }
    
    // If we already have an active integration, don't allow selecting a new one
    if (currentIntegration) {
      toast.info("Please deactivate the current integration before connecting a new one.");
      return;
    }
    
    // Reset selected integration first (to force re-render if clicking the same integration)
    setSelectedIntegration(null);
    
    // Use setTimeout to ensure the state update happens before setting the new value
    setTimeout(() => {
      //console.log(`Setting selected integration to ${integration.id}`);
      setSelectedIntegration(integration.id);
      
      // Use setTimeout to ensure the KnitAuth component has re-rendered
      setTimeout(() => {
        // Use the KnitAuth component ref to open the auth dialog
        if (knitAuthRef.current) {
          //console.log(`Opening auth for ${integration.id}`);
          knitAuthRef.current.openAuth();
        } else {
          //console.error('KnitAuth ref is not available');
          toast.error('Authentication component could not be initialized');
        }
      }, 10);
    }, 10);
  };

  return (
    <div className="space-y-4">
      {/* Current Integration */}
      {currentIntegration && (
        <div className="bg-white rounded-lg border p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Currently Integrated</h3>
          <div className="flex items-center gap-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white p-2 shadow-sm">
              <img 
                src={currentIntegration.logo} 
                alt={`${currentIntegration.appName} logo`} 
                className="w-12 h-12 object-contain"
                onError={(e) => {
                  // Use a fallback logo if the image fails to load
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-medium">{currentIntegration.appName || currentIntegration.appId}</h4>
              <p className="text-sm text-muted-foreground">Integration ID: {currentIntegration.integrationId}</p>
            </div>
            <Button 
              variant="destructive"
              onClick={() => setDeactivateDialogOpen(true)}
            >
              Deactivate Integration
            </Button>
          </div>
        </div>
      )}
      
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Integrate ATS</h2>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          {loading ? 'Loading ATS integrations...' : `${integrations.length} Available`}
        </p>
        
        <div className="relative mb-6">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            className="pl-10" 
            placeholder="Search ATS" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No ATS integrations available. Please check again later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredIntegrations.map((integration) => (
              <div 
                key={integration.id} 
                className="flex flex-col items-center justify-center border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer bg-[#f9f9fb] min-h-[160px] relative"
                onClick={() => handleIntegrationClick(integration)}
                data-testid="ats-integration-card"
              >
                {integration.integrated && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                )}
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white p-2 mb-3 shadow-sm">
                  <img 
                    src={integration.logo} 
                    alt={`${integration.name} logo`} 
                    className="w-12 h-12 object-contain"
                    onError={(e) => {
                      // Use a fallback logo if the image fails to load
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder.svg";
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-center">{integration.name}</span>
                {integration.integrated && (
                  <span className="text-xs text-green-600 mt-1">Connected</span>
                )}
              </div>
            ))}
            
            {/* Request new ATS integration box */}
            <div 
              className="flex flex-col items-center justify-center border border-dashed rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer bg-[#f9f9fb] min-h-[160px]"
              onClick={() => setRequestDialogOpen(true)}
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white p-2 mb-3 shadow-sm">
                <Plus className="w-8 h-8 text-gray-400" />
              </div>
              <span className="text-sm font-medium text-center">Can't find your ATS?</span>
              <span className="text-xs text-gray-500 mt-1 text-center">Request new integration</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Deactivate Integration Alert Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate ATS Integration</AlertDialogTitle>
            <AlertDialogDescription>
              This action will deactivate your ATS integration. All synced data from the ATS will be deleted from RoundOne and cannot be undone.
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeactivateIntegration}
              disabled={deactivating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deactivating ? "Deactivating..." : "Yes, Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* ATS Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Request ATS Integration</DialogTitle>
            <DialogDescription>
              Fill out this form to request a new ATS integration. Our team will review your request and get back to you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="atsName" className="text-left">
                ATS Name *
              </Label>
              <Input
                id="atsName"
                placeholder="e.g. Workable, JobAdder"
                value={atsName}
                onChange={(e) => setAtsName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="atsWebsite" className="text-left">
                ATS Website URL *
              </Label>
              <Input
                id="atsWebsite"
                placeholder="e.g. https://www.workable.com"
                value={atsWebsite}
                onChange={(e) => setAtsWebsite(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="message" className="text-left">
                Message
              </Label>
              <Textarea
                id="message"
                placeholder="Any additional details about your ATS integration request"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRequestDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitAtsRequest}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* KnitAuth component for handling the integration flow */}
      {selectedIntegration && (
        <KnitAuth 
          key={`knit-auth-${selectedIntegration}-${Date.now()}`}
          ref={knitAuthRef}
          appId={selectedIntegration}
          isAlreadyIntegrated={filteredIntegrations.find(i => i.id === selectedIntegration)?.integrated}
        />
      )}
    </div>
  );
};

export default IntegrationsTab;
